import { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { toast } from 'sonner';
import { useAuth } from './AuthContext';

const TeklifListesiContext = createContext();

export const useTeklifListesi = () => {
  const context = useContext(TeklifListesiContext);
  if (!context) {
    throw new Error('useTeklifListesi must be used within TeklifListesiProvider');
  }
  return context;
};

// localStorage'a güvenli yazma
const safeLocalStorageSet = (key, value) => {
  try {
    localStorage.setItem(key, value);
    return true;
  } catch (err) {
    if (err.name === 'QuotaExceededError' || err.code === 22 || err.code === 1014) {
      console.warn('LocalStorage dolu, temizleniyor...');
      // Tüm teklifListesi verilerini temizle
      const keysToRemove = [];
      for (let i = 0; i < localStorage.length; i++) {
        const k = localStorage.key(i);
        if (k && (k.startsWith('teklifListesi_') || k.startsWith('teklif_'))) {
          keysToRemove.push(k);
        }
      }
      keysToRemove.forEach(k => {
        try { localStorage.removeItem(k); } catch(e) { /* ignore */ }
      });
      // Tekrar dene
      try {
        localStorage.setItem(key, value);
        return true;
      } catch (e) {
        console.error('LocalStorage hala dolu:', e);
        return false;
      }
    }
    return false;
  }
};

export const TeklifListesiProvider = ({ children }) => {
  const { getUserFirma } = useAuth();
  const isInitialized = useRef(false);
  
  // Initial state from localStorage
  const getInitialState = () => {
    try {
      const firma = getUserFirma ? getUserFirma() : 'OTOMOTIV';
      const storageKey = `teklifListesi_${firma}`;
      const saved = localStorage.getItem(storageKey);
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          return parsed;
        }
      }
    } catch (err) {
      console.error('Teklif listesi yüklenemedi:', err);
    }
    return [];
  };
  
  const [teklifListesi, setTeklifListesi] = useState(getInitialState);

  // Firma kodunu al
  const getFirmaKey = useCallback(() => {
    const firma = getUserFirma ? getUserFirma() : 'OTOMOTIV';
    return `teklifListesi_${firma}`;
  }, [getUserFirma]);

  // LocalStorage'a kaydet (firma bazlı)
  useEffect(() => {
    if (!isInitialized.current) {
      isInitialized.current = true;
      return;
    }
    
    const storageKey = getFirmaKey();
    // Sadece minimum alanları kaydet
    const kompaktListe = teklifListesi.map(item => ({
      sku: item.sku,
      urun_adi: item.urun_adi?.substring(0, 100) || '', // Max 100 karakter
      liste_fiyati: item.liste_fiyati || 0,
      // KDV: Logo ERP'den gelen tax alanı gerçek değeri taşır.
      // kdv_orani yanlış senkronize edilmiş (her zaman 20), bu yüzden tax öncelikli.
      kdv_orani: item.kdv_orani || 20,
      birim: item.birim || 'ADET'
    }));
    
    const jsonStr = JSON.stringify(kompaktListe);
    const success = safeLocalStorageSet(storageKey, jsonStr);
    
    if (!success) {
      toast.error('Teklif listesi kaydedilemedi - Tarayıcı depolaması dolu');
    }
  }, [teklifListesi, getFirmaKey]);

  const teklifListesineEkle = (urun) => {
    const skuToCheck = urun.sku || urun.stok_kodu;
    const mevcutIndex = teklifListesi.findIndex(item => item.sku === skuToCheck);
    
    if (mevcutIndex !== -1) {
      toast.info('Bu ürün zaten teklif listesinde');
      return;
    }

    const yeniUrun = {
      sku: skuToCheck,
      urun_adi: urun.urun_adi || urun.stok_adi || '',
      liste_fiyati: urun.liste_fiyati || urun.birim_fiyat || 0,
      // KDV oranı: Logo ERP'den gelen 'tax' alanı doğru değeri taşır.
      // 'kdv_orani' alanı her zaman 20 olarak yanlış senkronize edilmiş, bu yüzden tax öncelikli.
      kdv_orani: urun.tax ?? urun.kdv_orani ?? 20,
      barkod: urun.barkod || '',
      birim: urun.birim || 'ADET',
      ekleme_zamani: new Date().toISOString()
    };

    setTeklifListesi(prev => [...prev, yeniUrun]);
    toast.success('Teklif listesine eklendi');
  };

  const teklifListesindenCikar = (sku) => {
    setTeklifListesi(prev => prev.filter(item => item.sku !== sku));
    toast.info('Teklif listesinden çıkarıldı');
  };

  const teklifListesiniTemizle = () => {
    setTeklifListesi([]);
    // localStorage'ı da temizle
    try {
      localStorage.removeItem(getFirmaKey());
    } catch(e) { /* ignore */ }
    toast.success('Teklif listesi temizlendi');
  };

  const value = {
    teklifListesi,
    teklifListesineEkle,
    teklifListesindenCikar,
    teklifListesiniTemizle,
    teklifSayisi: teklifListesi.length
  };

  return (
    <TeklifListesiContext.Provider value={value}>
      {children}
    </TeklifListesiContext.Provider>
  );
};
